import {
  EVTableContainer,
  FilterBlock,
  FloatingFilter,
  FuelTypeFilter,
  InnerBlockContainer,
  Switch,
  SwitchRow,
  SwitchSelection,
  TT,
  TickDate,
  Timeline,
  TimelineSelection,
  vehicleTypes,
} from "@components/atoms/data/Components"
import { Grid, Col } from "@components/layout/Grid"
import { useEvAvailability, useEvData, useGlossaryTerms } from "@hooks/queries"
import { colors, extractQuarterlyData, groupedByFuel, groupedByManufacturer, groupedByType } from "@utils/evDataHelpers"
import { EditableText, InnerBlocks, defineBlock } from "eddev/blocks"
import { useHydrating } from "eddev/routing"
import { Fragment, forwardRef, useEffect, useMemo, useRef, useState } from "react"
import type { ComponentPropsWithoutRef } from "react"
import { Chart as ChartJS, ChartData, ChartOptions, registerables } from "chart.js/auto"
import { Chart } from "react-chartjs-2"
import { Heading } from "@components/atoms/Heading"
import { VSpace } from "@components/layout/Spacer"
import { cn } from "@utils/tw"
import { ALL_FUELS, BEV, HFCEV, HYBRID, ICE, INFO, PHEV } from "@components/atoms/Icons"
import { AnimatePresence, useInView } from "framer-motion"
import { useIsMobile } from "@hooks/useViewportDerived"
import { Button } from "@components/atoms/Button"
import { CarTypeFilter } from "@components/atoms/data/CarTypeFilter"
import { Tooltip } from "react-tooltip"

export const meta: BlockMeta = {
  title: "EV Index Sales by Manufacturer",
  category: "common",
  icon: "editor-table",
  keywords: ["ev index"],
  tags: ["root"],
  editMode: "preview",
  conditions: {
    postTypes: ["page", "post", "data-embed"],
    multiple: false,
  },
}

ChartJS.register(...registerables)

export default defineBlock("data/evindex-sales-by-manufacturer", (props) => {
  const evData = useEvData(undefined)
  const evAvailability = useEvAvailability(undefined)
  const hydrating = useHydrating()
  const [currentPeriod, setCurrentPeriod] = useState<number | null>(null)
  const [salesAvailability, setSalesAvailability] = useState<boolean>(false)
  const [manufacturerVehicleType, setManufacturerVehicleType] = useState<boolean>(false)
  const [currentFuelType, setCurrentFuelType] = useState<"All fuels" | "ICE" | "BEV" | "PHEV" | "Hybrid" | "HFCEV">(
    "All fuels",
  )
  const [currentVehicleType, setCurrentVehicleType] = useState<(keyof typeof vehicleTypes)[]>(["all"])
  const [rolling, setRolling] = useState<"normal" | "rolling" | "ytd">("normal")
  const [currentYear, setCurrentYear] = useState<number | null>(null)
  const glossary = useGlossaryTerms(undefined).data?.siteSettings?.themeSiteSettings?.glossary

  const fuelType = useMemo(() => {
    if (evData.data?.evIndexData && evAvailability.data?.evIndexDataAvailability) {
      return {
        sales: groupedByFuel(evData.data.evIndexData),
        availability: groupedByFuel(evAvailability.data.evIndexDataAvailability),
      }
    }
  }, [evData.data?.evIndexData, evAvailability.data?.evIndexDataAvailability])

  const manufacturers = useMemo(() => {
    if (evData.data?.evIndexData && evAvailability.data?.evIndexDataAvailability) {
      return {
        sales: groupedByManufacturer(evData.data.evIndexData),
        availability: groupedByManufacturer(evAvailability.data.evIndexDataAvailability),
        type: groupedByType(evData.data.evIndexData),
        typeAvailibility: groupedByType(evAvailability.data.evIndexDataAvailability),
      }
    }
  }, [evData.data?.evIndexData, evAvailability.data?.evIndexDataAvailability])

  const vehicleType = useMemo(() => {
    if (evData.data?.evIndexData && evAvailability.data?.evIndexDataAvailability) {
      return {
        sales: groupedByType(evData.data.evIndexData),
        availability: groupedByType(evAvailability.data.evIndexDataAvailability),
      }
    }
  }, [evData.data?.evIndexData, evAvailability.data?.evIndexDataAvailability])

  const quarters = useMemo(() => {
    if (manufacturers) {
      const use = salesAvailability ? manufacturers.availability : manufacturers.sales
      return extractQuarterlyData(use[0].vehicles[0]).map((data) => data.quarter)
    }
    return []
  }, [manufacturers, salesAvailability])

  useEffect(() => {
    // Initial current quarter
    if (quarters.length > 0) {
      setCurrentPeriod(quarters.length - 1)
      setCurrentYear(parseInt(quarters[quarters.length - 1].split(" ")[1]))
    }
  }, [quarters])

  const sortedByQuarter = useMemo(() => {
    if (manufacturers && vehicleType && quarters.length > 0 && currentPeriod !== null) {
      const use = manufacturerVehicleType
        ? salesAvailability
          ? vehicleType.availability
          : vehicleType.sales
        : salesAvailability
          ? manufacturers.availability
          : manufacturers.sales

      if (rolling === "rolling") {
        if (currentFuelType === "All fuels") {
          if (currentVehicleType.includes("all")) {
            return use
              .map((manufacturer) => ({
                manufacturer: manufacturerVehicleType //@ts-ignore
                  ? manufacturer["VEHICLE TYPE"]
                  : //@ts-ignore
                    manufacturer.MANUFACTURER,
                total: manufacturer.vehicles
                  .map(
                    (vehicle) =>
                      vehicle[quarters[currentPeriod]] +
                      vehicle[quarters[currentPeriod - 1]] +
                      vehicle[quarters[currentPeriod - 2]] +
                      vehicle[quarters[currentPeriod - 3]],
                  )
                  .reduce((a, b) => a + b, 0),
              }))
              .filter((manufacturer) => manufacturer.total > 0)
              .sort((a, b) => b.total - a.total)
          }
          return (
            use
              //@ts-ignore
              .filter((data) => currentVehicleType.map((type) => vehicleTypes[type]).includes(data["VEHICLE TYPE"]))
              .map((manufacturer) => ({
                manufacturer: manufacturerVehicleType //@ts-ignore
                  ? manufacturer["VEHICLE TYPE"]
                  : //@ts-ignore
                    manufacturer.MANUFACTURER,
                total: manufacturer.vehicles
                  .map(
                    (vehicle) =>
                      vehicle[quarters[currentPeriod]] +
                      vehicle[quarters[currentPeriod - 1]] +
                      vehicle[quarters[currentPeriod - 2]] +
                      vehicle[quarters[currentPeriod - 3]],
                  )
                  .reduce((a, b) => a + b, 0),
              }))
              .filter((manufacturer) => manufacturer.total > 0)
              .sort((a, b) => b.total - a.total)
          )
        }

        if (currentVehicleType.includes("all")) {
          return use
            .map((manufacturer) => ({
              manufacturer: manufacturerVehicleType //@ts-ignore
                ? manufacturer["VEHICLE TYPE"]
                : //@ts-ignore
                  manufacturer.MANUFACTURER,
              total: manufacturer.vehicles
                .filter((vehicle) => vehicle["FUEL TYPE"] === currentFuelType)
                .map(
                  (vehicle) =>
                    vehicle[quarters[currentPeriod]] +
                    vehicle[quarters[currentPeriod - 1]] +
                    vehicle[quarters[currentPeriod - 2]] +
                    vehicle[quarters[currentPeriod - 3]],
                )
                .reduce((a, b) => a + b, 0),
            }))
            .filter((manufacturer) => manufacturer.total > 0)
            .sort((a, b) => b.total - a.total)
        }

        return (
          use
            //@ts-ignore
            .filter((data) => currentVehicleType.map((type) => vehicleTypes[type]).includes(data["VEHICLE TYPE"]))
            .map((manufacturer) => ({
              manufacturer: manufacturerVehicleType //@ts-ignore
                ? manufacturer["VEHICLE TYPE"]
                : //@ts-ignore
                  manufacturer.MANUFACTURER,
              total: manufacturer.vehicles
                .filter((vehicle) => vehicle["FUEL TYPE"] === currentFuelType)
                .map(
                  (vehicle) =>
                    vehicle[quarters[currentPeriod]] +
                    vehicle[quarters[currentPeriod - 1]] +
                    vehicle[quarters[currentPeriod - 2]] +
                    vehicle[quarters[currentPeriod - 3]],
                )
                .reduce((a, b) => a + b, 0),
            }))
            .filter((manufacturer) => manufacturer.total > 0)
            .sort((a, b) => b.total - a.total)
        )
      } else if (rolling === "normal") {
        if (currentFuelType === "All fuels") {
          if (currentVehicleType.includes("all")) {
            return use
              .map((manufacturer) => ({
                manufacturer: manufacturerVehicleType //@ts-ignore
                  ? manufacturer["VEHICLE TYPE"]
                  : //@ts-ignore
                    manufacturer.MANUFACTURER,
                total: manufacturer.vehicles
                  .map((vehicle) => vehicle[quarters[currentPeriod]])
                  .reduce((a, b) => a + b, 0),
              }))
              .filter((manufacturer) => manufacturer.total > 0)
              .sort((a, b) => b.total - a.total)
          }

          return (
            use
              //@ts-ignore
              .filter((data) => currentVehicleType.map((type) => vehicleTypes[type]).includes(data["VEHICLE TYPE"]))
              .map((manufacturer) => ({
                manufacturer: manufacturerVehicleType //@ts-ignore
                  ? manufacturer["VEHICLE TYPE"]
                  : //@ts-ignore
                    manufacturer.MANUFACTURER,
                total: manufacturer.vehicles
                  .map((vehicle) => vehicle[quarters[currentPeriod]])
                  .reduce((a, b) => a + b, 0),
              }))
              .filter((manufacturer) => manufacturer.total > 0)
              .sort((a, b) => b.total - a.total)
          )
        }

        if (currentVehicleType.includes("all")) {
          return use
            .map((manufacturer) => ({
              manufacturer: manufacturerVehicleType //@ts-ignore
                ? manufacturer["VEHICLE TYPE"]
                : //@ts-ignore
                  manufacturer.MANUFACTURER,
              total: manufacturer.vehicles
                .filter((vehicle) => vehicle["FUEL TYPE"] === currentFuelType)
                .map((vehicle) => vehicle[quarters[currentPeriod]])
                .reduce((a, b) => a + b, 0),
            }))
            .filter((manufacturer) => manufacturer.total > 0)
            .sort((a, b) => b.total - a.total)
        }

        return (
          use
            //@ts-ignore
            .filter((data) => currentVehicleType.map((type) => vehicleTypes[type]).includes(data["VEHICLE TYPE"]))
            .map((manufacturer) => ({
              manufacturer: manufacturerVehicleType //@ts-ignore
                ? manufacturer["VEHICLE TYPE"]
                : //@ts-ignore
                  manufacturer.MANUFACTURER,
              total: manufacturer.vehicles
                .filter((vehicle) => vehicle["FUEL TYPE"] === currentFuelType)
                .map((vehicle) => vehicle[quarters[currentPeriod]])
                .reduce((a, b) => a + b, 0),
            }))
            .filter((manufacturer) => manufacturer.total > 0)
            .sort((a, b) => b.total - a.total)
        )
      } else if (rolling === "ytd") {
        if (!currentYear) return []
        const currentYearQuarters = quarters.filter((q) => q.includes(currentYear.toString()))

        if (currentFuelType === "All fuels") {
          if (currentVehicleType.includes("all")) {
            return use
              .map((manufacturer) => ({
                manufacturer: manufacturerVehicleType //@ts-ignore
                  ? manufacturer["VEHICLE TYPE"]
                  : //@ts-ignore
                    manufacturer.MANUFACTURER,
                total: manufacturer.vehicles
                  .map((vehicle) => currentYearQuarters.reduce((sum, quarter) => sum + (vehicle[quarter] || 0), 0))
                  .reduce((a, b) => a + b, 0),
              }))
              .filter((manufacturer) => manufacturer.total > 0)
              .sort((a, b) => b.total - a.total)
          }

          return (
            use
              //@ts-ignore
              .filter((data) => currentVehicleType.map((type) => vehicleTypes[type]).includes(data["VEHICLE TYPE"]))
              .map((manufacturer) => ({
                manufacturer: manufacturerVehicleType //@ts-ignore
                  ? manufacturer["VEHICLE TYPE"]
                  : //@ts-ignore
                    manufacturer.MANUFACTURER,
                total: manufacturer.vehicles
                  .map((vehicle) => currentYearQuarters.reduce((sum, quarter) => sum + (vehicle[quarter] || 0), 0))
                  .reduce((a, b) => a + b, 0),
              }))
              .filter((manufacturer) => manufacturer.total > 0)
              .sort((a, b) => b.total - a.total)
          )
        }

        if (currentVehicleType.includes("all")) {
          return use
            .map((manufacturer) => ({
              manufacturer: manufacturerVehicleType //@ts-ignore
                ? manufacturer["VEHICLE TYPE"]
                : //@ts-ignore
                  manufacturer.MANUFACTURER,
              total: manufacturer.vehicles
                .filter((vehicle) => vehicle["FUEL TYPE"] === currentFuelType)
                .map((vehicle) => currentYearQuarters.reduce((sum, quarter) => sum + (vehicle[quarter] || 0), 0))
                .reduce((a, b) => a + b, 0),
            }))
            .filter((manufacturer) => manufacturer.total > 0)
            .sort((a, b) => b.total - a.total)
        }

        return (
          use
            //@ts-ignore
            .filter((data) => currentVehicleType.map((type) => vehicleTypes[type]).includes(data["VEHICLE TYPE"]))
            .map((manufacturer) => ({
              manufacturer: manufacturerVehicleType //@ts-ignore
                ? manufacturer["VEHICLE TYPE"]
                : //@ts-ignore
                  manufacturer.MANUFACTURER,
              total: manufacturer.vehicles
                .filter((vehicle) => vehicle["FUEL TYPE"] === currentFuelType)
                .map((vehicle) => currentYearQuarters.reduce((sum, quarter) => sum + (vehicle[quarter] || 0), 0))
                .reduce((a, b) => a + b, 0),
            }))
            .filter((manufacturer) => manufacturer.total > 0)
            .sort((a, b) => b.total - a.total)
        )
      }
    }
    return []
  }, [
    quarters,
    manufacturers,
    currentFuelType,
    manufacturerVehicleType,
    currentPeriod,
    rolling,
    currentVehicleType,
    currentYear,
  ])

  // Charts
  const chartRef = useRef<ChartJS<"doughnut">>(null)
  const chartTypeRef = useRef<ChartJS<"doughnut">>(null)

  const optionsBreakdown: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1,
    cutout: "60%",

    events: [],

    plugins: {
      tooltip: {
        enabled: false,
      },

      legend: {
        display: false,
      },
    },
  }

  const [centerText, setCenterText] = useState<string>("")
  const optionsManufacturer: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1,
    cutout: "60%",

    onHover: (event, element, chart) => {
      if (element.length > 0) {
        if (centerText !== currentData?.labels[element[0]?.index]) {
          setCenterText(currentData?.labels[element[0]?.index])
        }
      } else {
        setCenterText("")
      }
    },

    plugins: {
      tooltip: {
        enabled: false,
      },

      legend: {
        display: false,
      },
    },
  }

  const currentData = useMemo(() => {
    if (sortedByQuarter.length > 0) {
      return {
        labels: sortedByQuarter.map((manufacturers) => manufacturers.manufacturer),
        datasets: [
          {
            data: sortedByQuarter.map((manufacturers) => manufacturers.total),
            backgroundColor:
              currentFuelType === "All fuels" ? "#FEC32C" : colors[currentFuelType as keyof typeof colors],
            borderRadius: 4,
            borderWidth: 2,
          },
        ],
      }
    }
    return null
  }, [sortedByQuarter])

  const typeData = useMemo(() => {
    if (!currentYear) return null
    const currentYearQuarters = quarters.filter((q) => q.includes(currentYear.toString()))
    if (quarters.length > 0 && fuelType && currentPeriod !== null) {
      const use = salesAvailability ? fuelType.availability : fuelType.sales
      const sorted =
        rolling === "rolling"
          ? use
              .map((type) => ({
                fuelType: type["FUEL TYPE"],
                data: type.vehicles
                  .map(
                    (vehicle) =>
                      vehicle[quarters[currentPeriod]] +
                      vehicle[quarters[currentPeriod - 1]] +
                      vehicle[quarters[currentPeriod - 2]] +
                      vehicle[quarters[currentPeriod - 3]],
                  )
                  .reduce((a, b) => a + b, 0),
              }))
              .sort((a, b) => b.data - a.data)
          : rolling === "normal"
            ? use
                .map((type) => ({
                  fuelType: type["FUEL TYPE"],
                  data: type.vehicles.map((vehicle) => vehicle[quarters[currentPeriod]]).reduce((a, b) => a + b, 0),
                }))
                .sort((a, b) => b.data - a.data)
            : use
                .map((type) => ({
                  fuelType: type["FUEL TYPE"],
                  data: type.vehicles
                    .map((vehicle) =>
                      // Sum sales for all quarters in the current year
                      currentYearQuarters.reduce((sum, quarter) => sum + (vehicle[quarter] || 0), 0),
                    )
                    .reduce((a, b) => a + b, 0), // Sum across all vehicles of this fuel type
                }))
                .sort((a, b) => b.data - a.data)

      return {
        labels: sorted.map((type) => type.fuelType),
        datasets: [
          {
            data: sorted.map((type) => type.data),
            backgroundColor: sorted.map((type) => {
              const returnColor =
                currentFuelType === type.fuelType || currentFuelType === "All fuels"
                  ? type.fuelType
                  : `${type.fuelType}_50`
              return colors[returnColor as keyof typeof colors]
            }),
            borderRadius: 4,
            borderWidth: 2,
          },
        ],
      }
    }
    return null
  }, [quarters, fuelType, currentFuelType, currentPeriod, rolling])

  const isMobile = useIsMobile()
  const [filterOpen, setFilterOpen] = useState<boolean>(false)
  const blockRef = useRef<HTMLDivElement>(null)
  const inView = useInView(blockRef, { amount: 0.5 })

  const [titleMessage, setTitleMessage] = useState<number>(0)
  useEffect(() => {
    // 1 !manufacturerVehicleType !salesAvailability
    // 2 !manufacturerVehicleType salesAvailability
    // 3 manufacturerVehicleType !salesAvailability
    // 4 manufacturerVehicleType salesAvailability
    if (!manufacturerVehicleType && !salesAvailability) setTitleMessage(1)
    if (manufacturerVehicleType && !salesAvailability) setTitleMessage(2)
    if (!manufacturerVehicleType && salesAvailability) setTitleMessage(3)
    if (manufacturerVehicleType && salesAvailability) setTitleMessage(4)
  }, [manufacturerVehicleType, salesAvailability])

  return (
    <>
      <InnerBlockContainer
        className={cn(
          "[&>div]:hidden [&>div_.block-editor-inner-blocks]:w-full [&>div.--grid]:w-full [&>div_.--grid]:w-full",
          titleMessage === 1 && "[&>div:nth-of-type(1)]:flex",
          titleMessage === 2 && "[&>div:nth-of-type(2)]:flex",
          titleMessage === 3 && "[&>div:nth-of-type(3)]:flex",
          titleMessage === 4 && "[&>div:nth-of-type(4)]:flex",
        )}
      >
        <InnerBlocks />
      </InnerBlockContainer>
      <EVTableContainer ref={blockRef}>
        <div className="grid-auto px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px]">
          <Col initial={12} laptop={6}>
            {manufacturerVehicleType ? (
              <>
                {salesAvailability ? (
                  <EditableText as="p" placeholder="Enter content..." store="content_vt_av" />
                ) : (
                  <EditableText as="p" placeholder="Enter content..." store="content_vt_sales" />
                )}
              </>
            ) : (
              <>
                {salesAvailability ? (
                  <EditableText as="p" placeholder="Enter content..." store="content_av" />
                ) : (
                  <EditableText as="p" placeholder="Enter content..." store="content" />
                )}
              </>
            )}

            <VSpace initial={6} />
          </Col>

          {isMobile && (
            <AnimatePresence>
              {inView && (
                <FloatingFilter
                  open={filterOpen}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Button
                    size="tiny"
                    color="yellow"
                    icon={filterOpen ? "CARET_DOWN" : "CARET_UP"}
                    iconStyle="right"
                    onClick={() => setFilterOpen(!filterOpen)}
                  >
                    Filter this data
                  </Button>

                  <FuelTypeFilter>
                    <ul>
                      <li>
                        <button
                          className={`${currentFuelType === "All fuels" ? `active` : ``} all`}
                          onClick={() => setCurrentFuelType("All fuels")}
                        >
                          <i>{ALL_FUELS}</i>
                          <span>All fuels</span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`${currentFuelType === "ICE" ? `active` : ``} ice`}
                          onClick={() => setCurrentFuelType("ICE")}
                        >
                          <i>{ICE}</i>
                          <span>ICE</span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`${currentFuelType === "BEV" ? `active` : ``} bev`}
                          onClick={() => setCurrentFuelType("BEV")}
                        >
                          <i>{BEV}</i>
                          <span>BEV</span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`${currentFuelType === "PHEV" ? `active` : ``} phev`}
                          onClick={() => setCurrentFuelType("PHEV")}
                        >
                          <i>{PHEV}</i>
                          <span>PHEV</span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`${currentFuelType === "Hybrid" ? `active` : ``} hybrid`}
                          onClick={() => setCurrentFuelType("Hybrid")}
                        >
                          <i>{HYBRID}</i>
                          <span>Hybrid</span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`${currentFuelType === "HFCEV" ? `active` : ``} hfcev`}
                          onClick={() => setCurrentFuelType("HFCEV")}
                        >
                          <i>{HFCEV}</i>
                          <span>HFCEV</span>
                        </button>
                      </li>
                    </ul>
                  </FuelTypeFilter>

                  {manufacturerVehicleType && (
                    <CarTypeFilter
                      currentVehicleType={currentVehicleType}
                      setCurrentVehicleType={setCurrentVehicleType}
                    />
                  )}

                  <Switch>
                    <div>
                      <SwitchRow>
                        <SwitchSelection>
                          <label>
                            <input
                              type="radio"
                              name="salesAvailability"
                              value="sales"
                              defaultChecked
                              onChange={(e) => {
                                if (e.currentTarget.checked) {
                                  setSalesAvailability(false)
                                  setRolling("normal")
                                }
                              }}
                            />
                            Sales
                          </label>
                        </SwitchSelection>

                        <SwitchSelection>
                          <label>
                            <input
                              type="radio"
                              name="salesAvailability"
                              value="availability"
                              onChange={(e) => {
                                if (e.currentTarget.checked) setSalesAvailability(true)
                              }}
                            />
                            Availability
                          </label>
                        </SwitchSelection>
                      </SwitchRow>
                    </div>
                  </Switch>

                  <Switch>
                    <div>
                      <SwitchRow>
                        <SwitchSelection>
                          <label>
                            <input
                              type="radio"
                              name="manufacturerVehicleType"
                              value="manufacturer"
                              defaultChecked
                              onChange={(e) => {
                                if (e.currentTarget.checked) setManufacturerVehicleType(false)
                                setCurrentVehicleType(["all"])
                              }}
                            />
                            Manufacturer
                          </label>
                        </SwitchSelection>

                        <SwitchSelection>
                          <label>
                            <input
                              type="radio"
                              name="manufacturerVehicleType"
                              value="vehicleType"
                              onChange={(e) => {
                                if (e.currentTarget.checked) setManufacturerVehicleType(true)
                                setCurrentVehicleType(["all"])
                              }}
                            />
                            Vehicle Type
                          </label>
                        </SwitchSelection>
                      </SwitchRow>
                    </div>
                  </Switch>

                  {!salesAvailability && (
                    <Switch>
                      <div>
                        <SwitchRow>
                          <SwitchSelection>
                            <label>
                              <input
                                type="radio"
                                name="states"
                                value="quarterly"
                                defaultChecked
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setCurrentPeriod(quarters.length - 1)
                                    setRolling("normal")
                                  }
                                }}
                              />
                              Quarterly
                            </label>
                          </SwitchSelection>

                          <SwitchSelection>
                            <label>
                              <input
                                type="radio"
                                name="states"
                                value="rolling"
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setCurrentPeriod(quarters.length - 1)
                                    setRolling("rolling")
                                  }
                                }}
                              />
                              Rolling 4 Quarters
                            </label>
                          </SwitchSelection>

                          <SwitchSelection>
                            <label>
                              <input
                                type="radio"
                                name="states"
                                value="ytd"
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setCurrentPeriod(quarters.length - 1)
                                    setRolling("ytd")
                                  }
                                }}
                              />
                              Year to date
                            </label>
                          </SwitchSelection>
                        </SwitchRow>
                      </div>
                    </Switch>
                  )}

                  <TimelineSelection light>
                    {quarters.length > 0 && currentPeriod !== null && currentYear !== null && (
                      <Timeline className="ml-0">
                        <ul>
                          {(() => {
                            // Extract unique years from quarters
                            const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                            years.sort((a, b) => parseInt(a) - parseInt(b))

                            return rolling === "ytd"
                              ? years.map((year, i) => (
                                  <li key={i}>
                                    <TickDate
                                      className={year === currentYear.toString() ? "opacity-100" : "opacity-50"}
                                    >
                                      {year}
                                    </TickDate>
                                  </li>
                                ))
                              : quarters.map((period, i) => {
                                  if (rolling === "rolling" && [0, 1, 2].includes(i)) return <Fragment key={i} />
                                  return (
                                    <li key={i}>
                                      <TickDate
                                        className={
                                          i === currentPeriod
                                            ? "opacity-100"
                                            : i === 0
                                              ? currentPeriod === 1
                                                ? "opacity-0"
                                                : "opacity-50"
                                              : "opacity-0"
                                        }
                                      >
                                        {rolling === "rolling" ? (
                                          <>
                                            {quarters[i - 3]} - {period}
                                          </>
                                        ) : (
                                          period
                                        )}
                                      </TickDate>
                                    </li>
                                  )
                                })
                          })()}
                        </ul>

                        {rolling === "ytd" && (
                          <input
                            key="ytd"
                            type="range"
                            min={0}
                            max={(() => {
                              const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                              years.sort((a, b) => parseInt(a) - parseInt(b))
                              return years.length - 1
                            })()}
                            step={1}
                            defaultValue={currentYear}
                            list="period-marks"
                            onChange={(e) => {
                              const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                              years.sort((a, b) => parseInt(a) - parseInt(b))
                              setCurrentYear(parseInt(years[parseInt(e.target.value)]))
                            }}
                          />
                        )}
                        {rolling === "rolling" && (
                          <input
                            key="rolling"
                            type="range"
                            min={3}
                            max={quarters.length - 1}
                            step={1}
                            defaultValue={quarters.length - 1}
                            list="period-marks"
                            onChange={(e) => {
                              setCurrentPeriod(parseInt(e.target.value))
                            }}
                          />
                        )}

                        {rolling === "normal" && (
                          <input
                            key="normal"
                            type="range"
                            min={0}
                            max={quarters.length - 1}
                            step={1}
                            defaultValue={quarters.length - 1}
                            list="period-marks"
                            onChange={(e) => {
                              setCurrentPeriod(parseInt(e.target.value))
                            }}
                          />
                        )}
                      </Timeline>
                    )}
                  </TimelineSelection>
                </FloatingFilter>
              )}
            </AnimatePresence>
          )}
          {!isMobile && (
            <>
              <Col initial={12}>
                <FilterBlock>
                  <FuelTypeFilter>
                    <ul>
                      <li>
                        <button
                          className={`${currentFuelType === "All fuels" ? `active` : ``} all`}
                          onClick={() => setCurrentFuelType("All fuels")}
                        >
                          <i>{ALL_FUELS}</i>
                          <span>All fuels</span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`${currentFuelType === "ICE" ? `active` : ``} ice`}
                          onClick={() => setCurrentFuelType("ICE")}
                        >
                          <i>{ICE}</i>
                          <span>ICE</span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`${currentFuelType === "BEV" ? `active` : ``} bev`}
                          onClick={() => setCurrentFuelType("BEV")}
                        >
                          <i>{BEV}</i>
                          <span>BEV</span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`${currentFuelType === "PHEV" ? `active` : ``} phev`}
                          onClick={() => setCurrentFuelType("PHEV")}
                        >
                          <i>{PHEV}</i>
                          <span>PHEV</span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`${currentFuelType === "Hybrid" ? `active` : ``} hybrid`}
                          onClick={() => setCurrentFuelType("Hybrid")}
                        >
                          <i>{HYBRID}</i>
                          <span>Hybrid</span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`${currentFuelType === "HFCEV" ? `active` : ``} hfcev`}
                          onClick={() => setCurrentFuelType("HFCEV")}
                        >
                          <i>{HFCEV}</i>
                          <span>HFCEV</span>
                        </button>
                      </li>
                    </ul>
                  </FuelTypeFilter>

                  {manufacturerVehicleType && (
                    <CarTypeFilter
                      currentVehicleType={currentVehicleType}
                      setCurrentVehicleType={setCurrentVehicleType}
                    />
                  )}

                  <Switch>
                    <div>
                      <SwitchRow>
                        <SwitchSelection>
                          <label>
                            <input
                              type="radio"
                              name="salesAvailability"
                              value="sales"
                              defaultChecked
                              onChange={(e) => {
                                if (e.currentTarget.checked) {
                                  setSalesAvailability(false)
                                  setRolling("normal")
                                }
                              }}
                            />
                            Sales
                          </label>
                        </SwitchSelection>
                      </SwitchRow>

                      <SwitchRow border>
                        <SwitchSelection>
                          <label>
                            <input
                              type="radio"
                              name="salesAvailability"
                              value="availability"
                              onChange={(e) => {
                                if (e.currentTarget.checked) setSalesAvailability(true)
                              }}
                            />
                            Availability
                          </label>
                        </SwitchSelection>
                      </SwitchRow>
                    </div>
                  </Switch>

                  <Switch>
                    <div>
                      <SwitchRow>
                        <SwitchSelection>
                          <label>
                            <input
                              type="radio"
                              name="manufacturerVehicleType"
                              value="manufacturer"
                              defaultChecked
                              onChange={(e) => {
                                if (e.currentTarget.checked) setManufacturerVehicleType(false)
                                setCurrentVehicleType(["all"])
                              }}
                            />
                            Manufacturer
                          </label>
                        </SwitchSelection>
                      </SwitchRow>

                      <SwitchRow border>
                        <SwitchSelection>
                          <label>
                            <input
                              type="radio"
                              name="manufacturerVehicleType"
                              value="vehicleType"
                              onChange={(e) => {
                                if (e.currentTarget.checked) setManufacturerVehicleType(true)
                                setCurrentVehicleType(["all"])
                              }}
                            />
                            Vehicle Type
                          </label>
                        </SwitchSelection>
                      </SwitchRow>
                    </div>
                  </Switch>
                </FilterBlock>
              </Col>

              <Col initial={12}>
                <TimelineSelection light>
                  {!salesAvailability && (
                    <Switch>
                      <div>
                        <SwitchRow>
                          <SwitchSelection>
                            <label>
                              <input
                                type="radio"
                                name="states"
                                value="quarterly"
                                defaultChecked
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setCurrentPeriod(quarters.length - 1)
                                    setRolling("normal")
                                  }
                                }}
                              />
                              Quarterly
                            </label>
                          </SwitchSelection>

                          <SwitchSelection>
                            <label>
                              <input
                                type="radio"
                                name="states"
                                value="rolling"
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setCurrentPeriod(quarters.length - 1)
                                    setRolling("rolling")
                                  }
                                }}
                              />
                              Rolling 4 Quarters
                            </label>
                          </SwitchSelection>

                          <SwitchSelection>
                            <label>
                              <input
                                type="radio"
                                name="states"
                                value="ytd"
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setCurrentPeriod(quarters.length - 1)
                                    setRolling("ytd")
                                  }
                                }}
                              />
                              Year to date
                            </label>
                          </SwitchSelection>
                        </SwitchRow>
                      </div>
                    </Switch>
                  )}
                  {quarters.length > 0 && currentPeriod !== null && currentYear !== null && (
                    <Timeline className="ml-0">
                      <ul>
                        {(() => {
                          // Extract unique years from quarters
                          const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                          years.sort((a, b) => parseInt(a) - parseInt(b))

                          return rolling === "ytd"
                            ? years.map((year, i) => (
                                <li key={i}>
                                  <TickDate className={year === currentYear.toString() ? "opacity-100" : "opacity-50"}>
                                    {year}
                                  </TickDate>
                                </li>
                              ))
                            : quarters.map((period, i) => {
                                if (rolling === "rolling" && [0, 1, 2].includes(i)) return <Fragment key={i} />
                                return (
                                  <li key={i}>
                                    <TickDate
                                      className={
                                        i === currentPeriod
                                          ? "opacity-100"
                                          : i === 0
                                            ? currentPeriod === 1
                                              ? "opacity-0"
                                              : "opacity-50"
                                            : "opacity-0"
                                      }
                                    >
                                      {rolling === "rolling" ? (
                                        <>
                                          {quarters[i - 3]} - {period}
                                        </>
                                      ) : (
                                        period
                                      )}
                                    </TickDate>
                                  </li>
                                )
                              })
                        })()}
                      </ul>

                      {rolling === "ytd" && (
                        <input
                          key="ytd"
                          type="range"
                          min={0}
                          max={(() => {
                            const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                            years.sort((a, b) => parseInt(a) - parseInt(b))
                            return years.length - 1
                          })()}
                          step={1}
                          defaultValue={currentYear}
                          list="period-marks"
                          onChange={(e) => {
                            const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                            years.sort((a, b) => parseInt(a) - parseInt(b))
                            setCurrentYear(parseInt(years[parseInt(e.target.value)]))
                          }}
                        />
                      )}
                      {rolling === "rolling" && (
                        <input
                          key="rolling"
                          type="range"
                          min={3}
                          max={quarters.length - 1}
                          step={1}
                          defaultValue={quarters.length - 1}
                          list="period-marks"
                          onChange={(e) => {
                            setCurrentPeriod(parseInt(e.target.value))
                          }}
                        />
                      )}

                      {rolling === "normal" && (
                        <input
                          key="normal"
                          type="range"
                          min={0}
                          max={quarters.length - 1}
                          step={1}
                          defaultValue={quarters.length - 1}
                          list="period-marks"
                          onChange={(e) => {
                            setCurrentPeriod(parseInt(e.target.value))
                          }}
                        />
                      )}
                    </Timeline>
                  )}
                </TimelineSelection>
              </Col>
            </>
          )}

          <Col initial={12} />
          <Col initial={6} laptop={3}>
            <VSpace initial={6} />
            <Heading as="h4" variant="h4">
              Fuel type breakdown
            </Heading>
            <VSpace initial={6} />
            {typeData && (
              <ChartContainer>
                <Chart ref={chartTypeRef} type="doughnut" data={typeData} options={optionsBreakdown} />

                <Legend>
                  {typeData?.labels.map((label, i) => (
                    <li key={i}>
                      <Dot style={{ backgroundColor: colors[label as keyof typeof colors] }} />
                      {label}
                    </li>
                  ))}
                </Legend>
              </ChartContainer>
            )}
          </Col>
          <Col initial={6} laptop={3}>
            <VSpace initial={6} />
            <Heading as="h4" variant="h4">
              {currentFuelType}
            </Heading>
            <VSpace initial={6} />
            {currentData && (
              <VehicleChartContainer>
                <Chart ref={chartRef} type="doughnut" data={currentData} options={optionsManufacturer} />
                <CenterText>{centerText}</CenterText>
              </VehicleChartContainer>
            )}
          </Col>
          <Col initial={12} bigPhone={12} laptop={6}>
            {!hydrating && glossary && (
              <TableContainer>
                <table>
                  <thead>
                    <tr>
                      <th>{manufacturerVehicleType ? "Vehicle type" : "Manufacturer"}</th>
                      <th>
                        {manufacturerVehicleType ? (
                          <>
                            <TT>
                              {currentFuelType}
                              {currentFuelType !== "All fuels" && (
                                <>
                                  <a className="tt">{INFO}</a>
                                  <Tooltip anchorSelect=".tt" style={{ width: "250px", zIndex: 50 }}>
                                    {glossary.filter((term) => term?.term === currentFuelType)[0]?.description}
                                  </Tooltip>
                                </>
                              )}
                            </TT>
                          </>
                        ) : (
                          "Ranking"
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedByQuarter.map((manufacturer, i) => (
                      <tr
                        key={i}
                        onMouseOver={() => {
                          if (chartRef.current) {
                            chartRef.current.setActiveElements([
                              {
                                datasetIndex: 0,
                                index: i,
                              },
                            ])
                            chartRef.current.update()
                            setCenterText(manufacturer.manufacturer)
                          }
                        }}
                        onMouseOut={() => {
                          if (chartRef.current) {
                            chartRef.current.setActiveElements([])
                            chartRef.current.update()
                            setCenterText("")
                          }
                        }}
                      >
                        <td>{manufacturer.manufacturer}</td>
                        <td>
                          {manufacturerVehicleType ? (
                            <>
                              {((manufacturer.total / sortedByQuarter.reduce((a, b) => a + b.total, 0)) * 100).toFixed(
                                2,
                              )}
                              %
                            </>
                          ) : (
                            <>{i + 1}</>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableContainer>
            )}
          </Col>
          <Col initial={12}>
            <VSpace initial={6} />
          </Col>
        </div>
      </EVTableContainer>
    </>
  )
})

const VehicleChartContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("relative", className)} {...props} />,
)
VehicleChartContainer.displayName = "VehicleChartContainer"

const CenterText = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("type-title-s absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center", className)}
    {...props}
  />
))
CenterText.displayName = "CenterText"

const TableContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex relative overflow-scroll h-full my-6 min-h-[300px]",
      "[&_table]:absolute [&_table]:top-0 [&_table]:left-0 [&_table]:-mt-[2px]",
      "[&_table_thead]:sticky [&_table_thead]:top-0",
      className,
    )}
    {...props}
  />
))
TableContainer.displayName = "TableContainer"

const ChartContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("relative [&_canvas]:!w-full [&_canvas]:!h-auto", className)} {...props} />
))
ChartContainer.displayName = "ChartContainer"

const Legend = forwardRef<HTMLUListElement, ComponentPropsWithoutRef<"ul">>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn(
      "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 my-0",
      "[&_li]:flex [&_li]:gap-2 [&_li]:my-2 [&_li]:items-center [&_li]:type-label-s",
      className,
    )}
    {...props}
  />
))
Legend.displayName = "Legend"

const Dot = forwardRef<HTMLSpanElement, ComponentPropsWithoutRef<"span">>(({ className, ...props }, ref) => (
  <span ref={ref} className={cn("flex min-w-[12px] min-h-[12px] h-[12px] w-[12px] rounded-md", className)} {...props} />
))
Dot.displayName = "Dot"
